import { formatPropertyAddress, formatStreetAddress } from '../utils/propertyAddress';
import { FleetLandingHeader, FleetLandingDock } from './FleetLandingNavigation';
import LandingNavIcon from './LandingNavIcon';
import { propertyFetch } from '../services/idxPropertyClient';
// src/components/LandingPage.js
import React, { useState } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import ActionButtons from './ActionButtons';
import PropertyDetailModal from './PropertyDetailModal';
import usePropertyDetail from '../hooks/usePropertyDetail';
import BlogsPage from './BlogsPage';
import ReviewsPage from './ReviewsPage';
import SEOHelmet from './SEOHelmet';
import LocalResourceLinks from './LocalResourceLinks';
import { SITE_CONFIG } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import { requestPasswordReset, signIn, signUp } from '../services/authService';
import {
  clearAnalyticsUser,
  completeAnalyticsAuthentication,
  getAnalyticsIdentityForAuth
} from '../services/analyticsService';
import { submitChatLead } from '../services/chatLeadService';
import { selectFeaturedProperties } from '../utils/featuredProperties';
import './LandingPage.css';
import './IntroVideo.css';
import HoneypotField, { isHoneypotSubmission } from './HoneypotField';

import './FleetLandingNavigation.css';

const HeroReviewsWidget = ({ reviews, activeIndex, onSelect, onOpen, onPauseChange, mobile = false }) => {
  const hasReviews = Array.isArray(reviews) && reviews.length > 0;
  const isLoading = reviews === null;
  const activeReview = hasReviews ? reviews[activeIndex] : null;
  const reviewDate = activeReview?.created_at || activeReview?.created_date;
  const touchStartX = React.useRef(null);
  const showPrevious = () => hasReviews && onSelect((activeIndex - 1 + reviews.length) % reviews.length);
  const showNext = () => hasReviews && onSelect((activeIndex + 1) % reviews.length);

  return (
    <section
      className={`luxury-review-widget ${mobile ? 'luxury-review-widget-mobile' : ''}`}
      aria-label={`${SITE_CONFIG.AGENT.NAME} client reviews`}
      onMouseEnter={() => onPauseChange(true)}
      onMouseLeave={() => onPauseChange(false)}
      onTouchStart={(event) => { touchStartX.current = event.touches[0].clientX; }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null || !hasReviews) return;
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 36) delta < 0 ? showNext() : showPrevious();
        touchStartX.current = null;
      }}
    >
      <div className="luxury-review-widget-top">
        <div className="luxury-review-score">
          <strong>{SITE_CONFIG.LANDING_PAGE.REVIEW_SUMMARY.RATING}</strong>
          <span className="luxury-review-stars" role="img" aria-label={`${SITE_CONFIG.LANDING_PAGE.REVIEW_SUMMARY.RATING} out of 5 stars`}>★★★★★</span>
        </div>
        <a href={SITE_CONFIG.AGENT.ZILLOW_REVIEW} target="_blank" rel="noopener noreferrer">
          {SITE_CONFIG.LANDING_PAGE.REVIEW_SUMMARY.COUNT} {SITE_CONFIG.LANDING_PAGE.REVIEW_SUMMARY.SOURCE} reviews <span aria-hidden="true">↗</span>
        </a>
      </div>

      <div className="luxury-review-widget-body" aria-live="polite">
        {isLoading ? (
          <p className="luxury-review-status">Gathering client stories…</p>
        ) : hasReviews ? (
          <blockquote key={activeReview.id || activeIndex}>
            <p>“{activeReview.review_text}”</p>
            <footer>
              <strong>{activeReview.author || `${SITE_CONFIG.AGENT.NAME} client`}</strong>
              <span>{activeReview.detail || (reviewDate && new Date(reviewDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }))}</span>
              {activeReview.source_url && (
                <a href={activeReview.source_url} target="_blank" rel="noopener noreferrer">Source: {activeReview.source || 'Zillow'}</a>
              )}
            </footer>
          </blockquote>
        ) : (
          <p className="luxury-review-status">Worked with Peter? Your story can help the next Palm Beach County buyer or seller.</p>
        )}
      </div>

      <div className="luxury-review-widget-footer">
        {hasReviews && reviews.length > 1 ? (
          <div className="luxury-review-navigation">
            <button type="button" className="luxury-review-arrow" onClick={showPrevious} aria-label="Previous client review">←</button>
            <div className="luxury-review-dots" aria-label="Choose a client review">
              {reviews.map((review, index) => (
                <button
                  key={review.id || index}
                  type="button"
                  className={index === activeIndex ? 'active' : ''}
                  aria-label={`Show client review ${index + 1}`}
                  onClick={() => onSelect(index)}
                />
              ))}
            </div>
            <span className="luxury-review-count">{activeIndex + 1}/{reviews.length}</span>
            <button type="button" className="luxury-review-arrow" onClick={showNext} aria-label="Next client review">→</button>
          </div>
        ) : <span />}
        <button type="button" className="luxury-review-link" onClick={onOpen}>
          {hasReviews ? 'Read all reviews' : 'Share your experience'} <span aria-hidden="true">→</span>
        </button>
      </div>
    </section>
  );
};

const LandingPage = ({ directListing = false, directSearch = false }) => {
  const { listingKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const landingPageConfig = SITE_CONFIG.LANDING_PAGE;
  const [activeSection, setActiveSection] = useState(null);
  const [showMapPage, setShowMapPage] = useState(false);
  const [showGlobeMap, setShowGlobeMap] = useState(directListing || directSearch || location.state?.openGlobeMap);
  const [, setShowGlobeMapResults] = useState(false);
  const [selectedCity, setSelectedCity] = useState(location.state?.initialCity || null);
  const [showBlogs, setShowBlogs] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showSignInDropdown, setShowSignInDropdown] = useState(false);
  const [signInUsername, setSignInUsername] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [registerData, setRegisterData] = useState({
    username: '',
    email: '',
    password: '',
    phoneNumber: '',
    name: ''
  });
  const [showIntroVideo, setShowIntroVideo] = useState(!directListing && !directSearch);
  const [showMLSDisclosure, setShowMLSDisclosure] = useState(false);
  const [heroReviews, setHeroReviews] = useState(null);
  const [heroReviewIndex, setHeroReviewIndex] = useState(0);
  const [heroReviewsPaused, setHeroReviewsPaused] = useState(false);
  const homeValueVideoRef = React.useRef(null);
  const heroVideoRef = React.useRef(null);
  const imageWarmersRef = React.useRef([]);
  const warmedImageUrlsRef = React.useRef(new Set());
  const [showAgentSection, setShowAgentSection] = useState(false);
  const [showImagePageText, setShowImagePageText] = useState(false);
  const [showEPage, setShowEPage] = useState(false);
  const [showCityTiles, setShowCityTiles] = useState(false);
  const [featuredProperties, setFeaturedProperties] = useState([]);

  // Details and galleries load when a visitor opens a property.

  const [currentPropertyIndex, setCurrentPropertyIndex] = useState(0);
  const propertyTouchStartX = React.useRef(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectForm, setConnectForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    postalCode: '',
    smsConsent: false,
    serviceReq: 'buying'
  });
  const [submittingConnect, setSubmittingConnect] = useState(false);
  const [showConnectSuccess, setShowConnectSuccess] = useState(false);
  const [showHomeValueModal, setShowHomeValueModal] = useState(false);
  const [homeValueForm, setHomeValueForm] = useState({ name: '', email: '', phoneNumber: '', address: '', city: '', state: SITE_CONFIG.STATE, postalCode: '', smsConsent: false });
  const [submittingHomeValue, setSubmittingHomeValue] = useState(false);
  const [showHomeValueSuccess, setShowHomeValueSuccess] = useState(false);
  const [likedProperties, setLikedProperties] = useState(new Set());
  const { propertyDetail, closePropertyDetail, propertyDetailImages, imagesLoading, openPropertyFromSummary } = usePropertyDetail();

  // Service areas link to the data-driven neighborhood pages (/neighborhood/:slug).
  // Tile images are keyed per-area in config.js LANDING_PAGE.CITY_IMAGES.
  const SERVICE_AREAS = [
    { label: 'Delray Beach', slug: 'delray-beach', city: 'Delray Beach', state: 'FL', descriptor: 'Atlantic Avenue · Coastal living', imageUrl: landingPageConfig.CITY_IMAGES.DELRAY_BEACH },
    { label: 'Boca Raton', slug: 'boca-raton', city: 'Boca Raton', state: 'FL', descriptor: 'Waterfront · Clubs · Downtown', imageUrl: landingPageConfig.CITY_IMAGES.BOCA_RATON },
    { label: 'Boynton Beach', slug: 'boynton-beach', city: 'Boynton Beach', state: 'FL', descriptor: 'Marina life · Neighborhood variety', imageUrl: landingPageConfig.CITY_IMAGES.BOYNTON_BEACH },
    { label: 'Highland Beach', slug: 'highland-beach', city: 'Highland Beach', state: 'FL', descriptor: 'Oceanfront · Intracoastal', imageUrl: landingPageConfig.CITY_IMAGES.HIGHLAND_BEACH },
    { label: 'West Palm Beach', slug: 'west-palm-beach', city: 'West Palm Beach', state: 'FL', descriptor: 'Downtown · Historic · Waterfront', imageUrl: landingPageConfig.CITY_IMAGES.WEST_PALM_BEACH },
    { label: 'Palm Beach Gardens', slug: 'palm-beach-gardens', city: 'Palm Beach Gardens', state: 'FL', descriptor: 'Golf · Luxury · Planned living', imageUrl: landingPageConfig.CITY_IMAGES.PALM_BEACH_GARDENS },
    { label: 'Jupiter', slug: 'jupiter', city: 'Jupiter', state: 'FL', descriptor: 'Beaches · Boating · Parks', imageUrl: landingPageConfig.CITY_IMAGES.JUPITER },
    { label: 'Wellington', slug: 'wellington', city: 'Wellington', state: 'FL', descriptor: 'Equestrian · Estates · Family', imageUrl: landingPageConfig.CITY_IMAGES.WELLINGTON },
    { label: 'Lake Worth Beach', slug: 'lake-worth-beach', city: 'Lake Worth', state: 'FL', descriptor: 'Historic character · Coastal access', imageUrl: landingPageConfig.CITY_IMAGES.LAKE_WORTH_BEACH },
    { label: 'Fort Lauderdale', slug: 'fort-lauderdale', city: 'Fort Lauderdale', state: 'FL', descriptor: 'Broward · Waterfront · Urban', imageUrl: landingPageConfig.CITY_IMAGES.FORT_LAUDERDALE },
    { label: 'Miami', slug: 'miami', city: 'Miami', state: 'FL', descriptor: 'Miami-Dade · Global · Coastal', imageUrl: landingPageConfig.CITY_IMAGES.MIAMI },
    { label: 'Stuart', slug: 'stuart', city: 'Stuart', state: 'FL', descriptor: 'Martin County · Waterfront · Relaxed', imageUrl: landingPageConfig.CITY_IMAGES.STUART },
    { label: 'Port St. Lucie', slug: 'port-st-lucie', city: 'Port St. Lucie', state: 'FL', descriptor: 'St. Lucie · Space · Newer homes', imageUrl: landingPageConfig.CITY_IMAGES.PORT_ST_LUCIE }
  ];

  React.useEffect(() => {
    const video = heroVideoRef.current;
    if (!video || !showIntroVideo) return;
    video.load();
    video.play().catch(() => {});
  }, [showIntroVideo]);

  const handleAreaSearch = (event) => {const city=event.target.value;if(!city)return;event.target.value='';const area=SERVICE_AREAS.find(item=>item.city===city);if(!area)return;
 sessionStorage.removeItem('globeMapData');
 const params=new URLSearchParams({city:area.city,state:area.state || SITE_CONFIG.STATE,location:area.city,view:'map'});
 setShowGlobeMap(true);
 navigate(`/search?${params}`);
};

  const openAllHomes = () => {
    sessionStorage.removeItem('globeMapData');window.history.replaceState(window.history.state,'','/search');
    setSelectedCity(null);
    setShowGlobeMap(true);
  };

  React.useEffect(() => {
    if (showIntroVideo && !showGlobeMap) {
      document.body.classList.add('intro-active');
    } else {
      document.body.classList.remove('intro-active');
    }
  }, [showIntroVideo, showGlobeMap]);

  React.useEffect(() => {
    if (!showIntroVideo || showGlobeMap) return undefined;

    const connection = navigator.connection || {};
    if (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '')) return undefined;

    const warmImage = (src, priority = 'low') => {
      if (!src || warmedImageUrlsRef.current.has(src)) return;
      warmedImageUrlsRef.current.add(src);
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = priority;
      image.src = src;
      image.decode?.().catch(() => {});
      imageWarmersRef.current.push(image);
    };

    // Start the first scroll scene immediately after the hero paints so the large
    // editorial images are downloaded and decoded before the visitor reaches them.
    warmImage(landingPageConfig.EDITORIAL_IMAGES.LIVING_ROOM, 'high');
    warmImage(landingPageConfig.EDITORIAL_IMAGES.CONDO_INTERIOR, 'low');

    // The city tiles are several screens lower, so warm them without competing with
    // the hero and agent-story assets during the initial render.
    const warmCityTiles = () => {
      Object.values(landingPageConfig.CITY_IMAGES).forEach((src) => warmImage(src, 'low'));
    };
    const ric = window.requestIdleCallback;
    const id = ric ? ric(warmCityTiles, { timeout: 2500 }) : setTimeout(warmCityTiles, 1500);

    return () => {
      if (ric && window.cancelIdleCallback) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, [showIntroVideo, showGlobeMap, landingPageConfig]);

  React.useEffect(() => {
    if (!featuredProperties.length) return;
    const connection = navigator.connection || {};
    if (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '')) return;

    featuredProperties.slice(0, 3).forEach((property) => {
      if (!property.first_photo_url || warmedImageUrlsRef.current.has(property.first_photo_url)) return;
      warmedImageUrlsRef.current.add(property.first_photo_url);
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = 'low';
      image.src = property.first_photo_url;
      image.decode?.().catch(() => {});
      imageWarmersRef.current.push(image);
    });
  }, [featuredProperties]);

  React.useEffect(() => {
    if (!showIntroVideo) return undefined;
    const controller = new AbortController();

    const fetchHeroReviews = async () => {
      try {
        const response = await fetch(`https://tophamtechbe.com/api/treas/reviews/v1/get-review?site_id=${SITE_CONFIG.SITE_ID}`, {
          headers: { 'X-API-Key': 'treas-api-key-2025' },
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Reviews request failed with ${response.status}`);
        const data = await response.json();
        const firstPartyReviews = Array.isArray(data) ? data : [];
        setHeroReviews([...firstPartyReviews, ...landingPageConfig.FEATURED_REVIEWS].slice(0, 7));
      } catch (error) {
        if (error.name !== 'AbortError') setHeroReviews(landingPageConfig.FEATURED_REVIEWS);
      }
    };

    fetchHeroReviews();
    return () => controller.abort();
  }, [showIntroVideo, landingPageConfig.FEATURED_REVIEWS]);

  React.useEffect(() => {
    if (!Array.isArray(heroReviews) || heroReviews.length < 2 || heroReviewsPaused) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = setInterval(() => {
      setHeroReviewIndex((current) => (current + 1) % heroReviews.length);
    }, 6500);
    return () => clearInterval(id);
  }, [heroReviews, heroReviewsPaused]);

  React.useEffect(() => {
    // Hide intro video when on a direct listing route
    if (directListing && listingKey) {
      setShowIntroVideo(false);
      setShowGlobeMap(true);
    }
  }, [directListing, listingKey]);

  React.useEffect(() => {
    const handleScroll = () => {
      const viewportHeight = window.innerHeight;

      if (showIntroVideo && !showGlobeMap) {
        // Small screens use flowing sections that can grow with their content.
        // Reveal from real positions rather than assuming each section is 100vh.
        const reached = (id) => {
          const section = document.getElementById(id);
          return Boolean(section && section.getBoundingClientRect().top <= viewportHeight * 0.7);
        };
        setShowAgentSection(reached('agent-story'));
        setShowImagePageText(reached('featured-properties'));
        setShowEPage(reached('home-value'));
        setShowCityTiles(reached('service-areas'));
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [showIntroVideo, showGlobeMap]);

  React.useEffect(() => {
    let raf = null;
    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
    const update = () => {
      raf = null;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const viewportHeight = window.innerHeight;
      // Distance (px) from the moment the featured section is centered in the viewport.
      const imgParallax = scrollTop - viewportHeight * 2;
      const agentParallax = clamp((scrollTop - viewportHeight) * 0.055, -38, 38);
      const homeValueParallax = clamp((scrollTop - viewportHeight * 3) * 0.035, -24, 24);
      document.documentElement.style.setProperty('--img-parallax', String(imgParallax));
      document.documentElement.style.setProperty('--agent-primary-parallax', `${agentParallax}px`);
      document.documentElement.style.setProperty('--agent-secondary-parallax', `${agentParallax * -0.65}px`);
      document.documentElement.style.setProperty('--home-value-parallax', `${homeValueParallax}px`);
    };
    const onScroll = () => {
      if (raf == null) raf = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf != null) cancelAnimationFrame(raf);
    };
  }, []);

  React.useEffect(() => {
    const video = homeValueVideoRef.current;
    if (!video || !showEPage) return undefined;

    const connection = navigator.connection || {};
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const shouldKeepPoster = reduceMotion || connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '');

    if (shouldKeepPoster) {
      video.pause();
      return undefined;
    }

    const playPromise = video.play();
    if (playPromise?.catch) playPromise.catch(() => {});

    return () => video.pause();
  }, [showEPage]);

  React.useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  React.useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setIsAdmin(userData.roles?.includes('ROLE_ADMIN') || userData.roles?.includes('ROLE_ASSISTANT') || false);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
      }
    }

    // Fetch featured properties
    const fetchFeaturedProperties = async () => {
      try {
        const fetchMlsProperties = async (filters, limit = 25) => {
          const queryParams = new URLSearchParams({
            ...filters,
            status: 'Active',
            listingDateSort: 'newold',
            firstPhoto: 'true',
            hasMedia: 'true',
            pageSize: String(Math.min(limit, 100)),
            state: SITE_CONFIG.STATE
          });
          const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary/page?${queryParams}`, {});

          if (!response.ok) {
            throw new Error(`Featured property request failed: ${response.status}`);
          }

          return (await response.json()).properties || [];
        };

        // Curate the freshest presentation-ready homes across Peter's full service area.
        // This intentionally avoids leading with office inventory and keeps the gallery
        // visually consistent with a $1M+ floor, as requested in the site review.
        const counties = ['Palm Beach', 'Broward', 'Miami-Dade', 'Martin', 'St. Lucie'];
        const marketResults = await Promise.allSettled(counties.map((county) => fetchMlsProperties({
          county,
          propertyType: 'Residential',
          minPrice: '1000000'
        }, 20)));
        const marketProperties = marketResults
          .filter((result) => result.status === 'fulfilled')
          .flatMap((result) => result.value);
        if (!marketProperties.length && marketResults.every((result) => result.status === 'rejected')) {
          throw new Error('Unable to load featured South Florida properties');
        }

        setFeaturedProperties(selectFeaturedProperties(marketProperties));
      } catch (error) {
        console.error('Error fetching featured properties:', error);
      }
    };

    fetchFeaturedProperties();

    // Handle direct listing access
    if (directListing && listingKey) {
      // Store listing key for ActionButtons to use
      sessionStorage.setItem('directListingKey', listingKey);
    }

    // Track page view
    const trackPageView = async () => {
      try {
        await fetch('https://tophamtechbeb.com/aw-monitor/v1/page-views', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            site_id: SITE_CONFIG.SITE_ID,
            page_path: window.location.pathname,
            referrer: document.referrer || '',
            is_mobile: /Mobi|Android/i.test(navigator.userAgent),
            user_agent: navigator.userAgent,
            session_id: sessionStorage.getItem('session_id') || Math.random().toString(36).substr(2, 9)
          })
        });
      } catch (error) {
        console.error('Error tracking page view:', error);
      }
    };

    if (!sessionStorage.getItem('session_id')) {
      sessionStorage.setItem('session_id', Math.random().toString(36).substr(2, 9));
    }

    trackPageView();

    // Listen for globe map open event from AgentInfo
    const handleOpenGlobeMap = () => {
      setShowGlobeMap(true);
    };

    window.addEventListener('openGlobeMap', handleOpenGlobeMap);

    return () => {
      window.removeEventListener('openGlobeMap', handleOpenGlobeMap);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- page/session tracking is mount-only

  const handleToggle = () => {
    console.log('Toggle clicked, current mode:', isDarkMode);
    toggleTheme();
  };

  const handleSignInToggle = () => {
    setShowSignInDropdown(!showSignInDropdown);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    try {
      const analyticsIdentity = await getAnalyticsIdentityForAuth();
      const response = await signIn({
        username: signInUsername,
        password: signInPassword,
        siteId: SITE_CONFIG.SITE_ID,
        ...(analyticsIdentity ? { analyticsIdentity } : {})
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAdmin(userData.roles?.includes('ROLE_ADMIN') || userData.roles?.includes('ROLE_ASSISTANT') || false);
        localStorage.setItem('user', JSON.stringify(userData));
        completeAnalyticsAuthentication(userData, 'login');
        setShowSignInDropdown(false);
        setShowForgotPassword(false);
        setSignInUsername('');
        setSignInPassword('');
        setResetEmail('');
      } else {
        alert('Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    try {
      const analyticsIdentity = await getAnalyticsIdentityForAuth();
      const response = await signUp({
        ...registerData,
        siteId: SITE_CONFIG.SITE_ID,
        ...(analyticsIdentity ? { analyticsIdentity } : {})
      });

      if (response.ok) {
        // Auto sign in after registration
        const signInResponse = await signIn({
          username: registerData.username,
          password: registerData.password,
          siteId: SITE_CONFIG.SITE_ID,
          ...(analyticsIdentity ? { analyticsIdentity } : {})
        });

        if (signInResponse.ok) {
          const userData = await signInResponse.json();
          setUser(userData);
          setIsAdmin(userData.roles?.includes('ROLE_ADMIN') || userData.roles?.includes('ROLE_ASSISTANT') || false);
          localStorage.setItem('user', JSON.stringify(userData));
          completeAnalyticsAuthentication(userData, 'sign_up');
          setShowSignInDropdown(false);
          setRegisterData({ username: '', email: '', password: '', phoneNumber: '', name: '' });
        }
      } else {
        alert('Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      alert('Registration failed. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleLogOut = () => {
    clearAnalyticsUser();
    setUser(null);
    setIsAdmin(false);
    setShowSignInDropdown(false);
    localStorage.removeItem('user');
  };

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await requestPasswordReset({
        email: resetEmail,
        baseUrl: window.location.origin,
        siteId: SITE_CONFIG.SITE_ID
      });

      if (response.ok) {
        setResetSuccess(true);
      } else {
        alert('Failed to send reset link. Please try again.');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      alert('Failed to send reset link. Please try again.');
    }
  };

  const handleHomeValueSubmit = async (e) => {
    e.preventDefault();
    if (isHoneypotSubmission(e.currentTarget)) {
      setShowHomeValueModal(false);
      setHomeValueForm({ name: '', email: '', phoneNumber: '', address: '', city: '', state: SITE_CONFIG.STATE, postalCode: '', smsConsent: false });
      setShowHomeValueSuccess(true);
      setTimeout(() => setShowHomeValueSuccess(false), 6000);
      return;
    }

    setSubmittingHomeValue(true);
    try {
      const state = homeValueForm.state.trim().toUpperCase();
      const postalCode = homeValueForm.postalCode.trim();
      const propertyAddress = `${homeValueForm.address.trim()}, ${homeValueForm.city.trim()}, ${state} ${postalCode}`;
      const params = new URLSearchParams({
        postalCode,
        state,
        name: homeValueForm.name.trim(),
        phoneNumber: homeValueForm.phoneNumber.trim(),
        smsConsent: homeValueForm.smsConsent ? 'true' : 'false',
        serviceReq: `Home estimate requested — Property: ${propertyAddress}`,
        email: homeValueForm.email.trim(),
        siteId: SITE_CONFIG.SITE_ID
      });
      const response = await submitChatLead(params, { rememberContact: true });
      if (response.ok) {
        setShowHomeValueModal(false);
        setHomeValueForm({ name: '', email: '', phoneNumber: '', address: '', city: '', state: SITE_CONFIG.STATE, postalCode: '', smsConsent: false });
        setShowHomeValueSuccess(true);
        setTimeout(() => setShowHomeValueSuccess(false), 6000);
      } else {
        alert('Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Home value form error:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmittingHomeValue(false);
    }
  };

  const handleConnectSubmit = async (e) => {
    e.preventDefault();
    if (isHoneypotSubmission(e.currentTarget)) {
      setShowConnectModal(false);
      setConnectForm({ name: '', email: '', phoneNumber: '', postalCode: '', smsConsent: false, serviceReq: 'buying' });
      setShowConnectSuccess(true);
      setTimeout(() => setShowConnectSuccess(false), 5000);
      return;
    }

    setSubmittingConnect(true);
    try {
      const params = new URLSearchParams({
        postalCode: connectForm.postalCode,
        name: connectForm.name,
        phoneNumber: connectForm.phoneNumber,
        smsConsent: connectForm.smsConsent ? 'true' : 'false',
        serviceReq: connectForm.serviceReq,
        email: connectForm.email,
        siteId: SITE_CONFIG.SITE_ID
      });

      const response = await submitChatLead(params, { rememberContact: true });

      if (response.ok) {
        setShowConnectModal(false);
        setShowConnectSuccess(true);
        setConnectForm({ name: '', email: '', phoneNumber: '', postalCode: '', smsConsent: false, serviceReq: 'buying' });
        setTimeout(() => setShowConnectSuccess(false), 5000);
      } else {
        alert('Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Connect form error:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmittingConnect(false);
    }
  };

  return (
    <div className="landing-page">
      <SEOHelmet
        title={directSearch
          ? `Palm Beach County Property Search | ${SITE_CONFIG.AGENT.NAME}`
          : directListing
            ? `South Florida Property Details | ${SITE_CONFIG.AGENT.BROKERAGE}`
            : 'Luxury Real Estate Brokerage in Palm Beach County | Integritos Real Estate'}
        description={directSearch
          ? 'Search live Palm Beach County and South Florida homes, condos, waterfront properties, and investment opportunities.'
          : 'Buy, sell, or invest across Palm Beach, Broward, Miami-Dade, Martin, and St. Lucie counties with broker Peter Mergenthaler Jr of Integritos Real Estate.'}
        path={directSearch ? '/search' : directListing && listingKey ? `/listing/${listingKey}` : '/'}
      />
      <FleetLandingDock hidden={showBlogs || showReviews || showGlobeMap || showMapPage} isDarkMode={isDarkMode} onSearch={() => {sessionStorage.removeItem('globeMapData');window.history.replaceState(window.history.state,'','/search');setSelectedCity(null);setShowGlobeMap(true);}} onChat={() => navigate('/chat')} onJournal={() => navigate('/blogs')} onReviews={() => navigate('/reviews')} onCloseAccount={() => setShowSignInDropdown(false)} isAdmin={isAdmin} onAdmin={() => { window.location.href = '/admin'; }}><div className="signin-dropdown"><button type="button" className="fleet-dock-button signin-toggle" onClick={handleSignInToggle} aria-expanded={showSignInDropdown} aria-haspopup="dialog"><LandingNavIcon name="account" /><span>{user ? 'Account' : 'Sign in'}</span></button>{showSignInDropdown && (
            <div className="signin-dropdown-content" role="dialog" aria-label={user ? 'Account menu' : 'Sign in'}>
              {!user ? (
                showForgotPassword ? (
                  resetSuccess ? (
                    <div style={{textAlign: 'center', padding: '20px'}}>
                      <h3 style={{color: '#28a745', margin: '0 0 15px 0'}}>✓ Email Sent!</h3>
                      <p style={{margin: '0 0 20px 0', fontSize: '14px', color: '#666'}}>Please check your email for password reset instructions.</p>
                      <button
                        onClick={() => { setShowForgotPassword(false); setResetEmail(''); setResetSuccess(false); }}
                        className="toggle-form-btn"
                      >
                        Back to Sign In
                      </button>
                    </div>
                  ) : (
                    <div>
                      <form onSubmit={handlePasswordResetSubmit} className="signin-form">
                        <h3>Reset Password</h3>
                        <input
                          type="email"
                          placeholder="Enter your email"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          required
                        />
                        <button type="submit">
                          Send Reset Link
                        </button>
                      </form>
                      <button
                        onClick={() => { setShowForgotPassword(false); setResetEmail(''); }}
                        className="toggle-form-btn"
                      >
                        Back to Sign In
                      </button>
                    </div>
                  )
                ) : !showRegisterForm ? (
                  <div>
                    <form onSubmit={handleSignIn} className="signin-form">
                      <h3>Sign In</h3>
                      <input
                        type="text"
                        placeholder="Username or Email"
                        value={signInUsername}
                        onChange={(e) => setSignInUsername(e.target.value)}
                        required
                      />
                      <input
                        type="password"
                        placeholder="Password"
                        value={signInPassword}
                        onChange={(e) => setSignInPassword(e.target.value)}
                        required
                      />
                      <button type="submit" disabled={signingIn}>
                        {signingIn ? 'Signing In...' : 'Sign In'}
                      </button>
                    </form>
                    <button
                      onClick={() => setShowRegisterForm(true)}
                      className="toggle-form-btn"
                    >
                      Need an account? Register
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      style={{background: 'none', border: 'none', color: '#666', fontSize: '11px', textDecoration: 'underline', cursor: 'pointer', marginTop: '12px', padding: '4px 0'}}
                    >
                      Forgot Password?
                    </button>
                  </div>
                ) : (
                  <div>
                    <form onSubmit={handleRegister} className="signin-form">
                      <h3>Register</h3>
                      <input
                        type="text"
                        placeholder="Username"
                        value={registerData.username}
                        onChange={(e) => setRegisterData({...registerData, username: e.target.value})}
                        required
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                        required
                      />
                      <input
                        type="password"
                        placeholder="Password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                        required
                      />
                      <input
                        type="tel"
                        placeholder="Phone Number"
                        value={registerData.phoneNumber}
                        onChange={(e) => setRegisterData({...registerData, phoneNumber: e.target.value})}
                        required
                      />
                      <input
                        type="text"
                        placeholder="Full Name"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                        required
                      />
                      <button type="submit" disabled={signingIn}>
                        {signingIn ? 'Registering...' : 'Register'}
                      </button>
                    </form>
                    <button
                      onClick={() => setShowRegisterForm(false)}
                      className="toggle-form-btn"
                    >
                      Have an account? Sign In
                    </button>
                  </div>
                )
              ) : (
                <div className="user-info">
                  <h3>User Info</h3>
                  <p><strong>Name:</strong> {user.username}</p>
                  <p><strong>Email:</strong> {user.email}</p>
                  <p><strong>Role:</strong> {user.roles?.join(', ')}</p>
                  <button onClick={handleLogOut} className="logout-btn">
                    Log Off
                  </button>
                </div>
              )}
            </div>
          )}</div></FleetLandingDock>
      <div className={`fullscreen-chat-container ${showIntroVideo ? 'landing-story' : ''}`}>
        {showIntroVideo && (
          <div className="intro-video-overlay">
            <video
              ref={heroVideoRef}
              className="luxury-hero-video"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster={landingPageConfig.EDITORIAL_IMAGES.LIVING_ROOM}
              aria-hidden="true"
            >
              <source media="(max-width: 640px)" src={landingPageConfig.VIDEOS.MOBILE} type="video/mp4" />
              <source media="(max-width: 1024px)" src={landingPageConfig.VIDEOS.TABLET} type="video/mp4" />
              <source src={landingPageConfig.VIDEOS.DESKTOP} type="video/mp4" />
            </video>
            <div className="luxury-hero-scrim" aria-hidden="true" />
            <div className="luxury-hero-grain" aria-hidden="true" />
            <div className="luxury-hero-monogram" aria-hidden="true">PM</div>

            {!showGlobeMap && !showMapPage && <><FleetLandingHeader brand={<button type="button" className="luxury-wordmark" onClick={() => navigate('/agent')} aria-label={`Learn about ${SITE_CONFIG.AGENT.NAME}`}>
                <img
                  src={landingPageConfig.BROKERAGE_LOGO}
                  alt="Integritos Real Estate"
                  className="luxury-brokerage-logo"
                />
                <span className="luxury-wordmark-copy">
                  <span className="luxury-wordmark-company">{SITE_CONFIG.AGENT.BROKERAGE}</span>
                  <span className="luxury-wordmark-name">Luxury real estate brokerage</span>
                </span>
              </button>} onAbout={() => navigate('/agent')} onGuides={() => navigate('/guides')} onContact={() => setShowConnectModal(true)} onToggleTheme={handleToggle} isDarkMode={isDarkMode} /></>}

            <main className="luxury-hero-content">
              <div className="luxury-hero-copy">
                <span className="luxury-hero-eyebrow">Luxury real estate brokerage · Palm Beach County</span>
                <h1>South Florida real estate,<br /><em>precisely handled.</em></h1>
                <p className="luxury-hero-subtitle">34 years of real estate expertise, financial perspective, and strategic judgment for buyers, sellers, and investors across Palm Beach, Broward, Miami-Dade, Martin, and St. Lucie counties.</p>

                <div className="luxury-hero-proofline" aria-label="Peter's experience and approach">
                  <span><strong>34</strong> years in market</span>
                  <span><strong>Broker</strong> led service</span>
                  <span><strong>5</strong> counties served</span>
                </div>

                <div className="luxury-hero-actions">
                  <button type="button" className="luxury-primary-cta" onClick={openAllHomes}>
                    Search South Florida Homes <span aria-hidden="true">↗</span>
                  </button>
                  <button type="button" className="luxury-secondary-cta" onClick={() => setShowConnectModal(true)}>
                    Speak With Peter
                  </button>
                  <button type="button" className="luxury-concierge-cta" onClick={() => navigate('/chat')}>
                    <span aria-hidden="true">✦</span> AI Concierge
                  </button>
                </div>

                <div className="luxury-area-search">
                  <select
                    className="luxury-area-search-select"
                    onChange={handleAreaSearch}
                    defaultValue=""
                    aria-label="Search Palm Beach and South Florida homes by city"
                  >
                    <option value="" disabled>Search South Florida by city...</option>
                    {SERVICE_AREAS.map((area) => (
                      <option key={area.city} value={area.city}>{area.label}, FL</option>
                    ))}
                  </select>
                  <svg className="luxury-area-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </div>

                <HeroReviewsWidget
                  reviews={heroReviews}
                  activeIndex={heroReviewIndex}
                  onSelect={setHeroReviewIndex}
                  onOpen={() => navigate('/reviews')}
                  onPauseChange={setHeroReviewsPaused}
                  mobile
                />
              </div>

              <aside className="luxury-hero-rail">
                <div className="luxury-advisor-card">
                  <div className="luxury-advisor-photo-wrap">
                    <img src={landingPageConfig.AGENT_IMAGE} alt={SITE_CONFIG.AGENT.NAME} className="luxury-advisor-photo" />
                  </div>
                  <div className="luxury-advisor-details">
                    <span className="luxury-advisor-overline">Your South Florida broker</span>
                    <h2>{SITE_CONFIG.AGENT.NAME}</h2>
                    <p>{SITE_CONFIG.AGENT.BROKERAGE}</p>
                    <div className="luxury-advisor-rule" />
                    <dl>
                      <div><dt>Experience</dt><dd>34 years</dd></div>
                      <div><dt>License</dt><dd>FL #{SITE_CONFIG.AGENT.LICENSE}</dd></div>
                    </dl>
                    <button type="button" onClick={() => navigate('/agent')}>Meet Peter <span aria-hidden="true">→</span></button>
                  </div>
                </div>
                <HeroReviewsWidget
                  reviews={heroReviews}
                  activeIndex={heroReviewIndex}
                  onSelect={setHeroReviewIndex}
                  onOpen={() => navigate('/reviews')}
                  onPauseChange={setHeroReviewsPaused}
                />
              </aside>
            </main>
            <div className="luxury-scroll-cue" aria-hidden="true"><span>Discover more</span><i /></div>
          </div>
        )}
        {showIntroVideo && (
          <>
            <div className="agent-intro-page" id="agent-story">
              {showAgentSection && (
                <div className="agent-editorial-layout">
                  <div className="agent-editorial-images" aria-hidden="true">
                    <div className="agent-editorial-image agent-editorial-image-primary">
                      <video
                        className="agent-editorial-video"
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        poster={landingPageConfig.EDITORIAL_IMAGES.LIVING_ROOM}
                      >
                        <source src={landingPageConfig.VIDEOS.SHOWCASE} type="video/mp4" />
                      </video>
                      <span className="agent-editorial-video-label">Palm Beach County, in motion</span>
                    </div>
                    <div className="agent-editorial-image agent-editorial-image-secondary">
                      <img src={landingPageConfig.EDITORIAL_IMAGES.CONDO_INTERIOR} alt="" decoding="async" fetchPriority="low" />
                    </div>
                    <span className="agent-editorial-caption">Delray Beach · Palm Beach County · South Florida</span>
                  </div>
                  <div className="agent-intro-card">
                    <span className="agent-intro-eyebrow">Local knowledge · Strategic perspective</span>
                    <div className="agent-intro-heading">
                      <img src={landingPageConfig.AGENT_IMAGE} alt={SITE_CONFIG.AGENT.NAME} className="agent-intro-photo" />
                      <div className="agent-intro-text">
                        <span>Your South Florida real estate advisor</span>
                        <h2>Meet {SITE_CONFIG.AGENT.NAME}</h2>
                      </div>
                    </div>
                    <p className="agent-intro-copy">34 years of real estate experience, strengthened by a background in mortgage lending, financial markets and complex transactions. Strategic guidance for every real estate decision.</p>
                    <button className="agent-intro-btn" onClick={() => navigate('/agent')}>Discover Peter's Approach</button>
                  </div>
                </div>
              )}
            </div>
            <div className={`image-page ${showImagePageText ? 'visible' : ''}`} id="featured-properties">
              {showImagePageText && (
                <>
                  <div className="featured-section-ornaments" aria-hidden="true">
                    <div className="featured-flourish featured-flourish-top">
                      <span></span>
                      <span></span>
                    </div>
                    <span className="featured-tulsa-watermark">South Florida</span>
                    <div className="featured-flourish featured-flourish-bottom">
                      <span></span>
                      <span></span>
                    </div>
                    <div className="featured-ornament-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <span className="featured-ornament-baseline"></span>
                  </div>
                  <Link to="/chat" className="image-page-text relocation-card" aria-label="Plan your Palm Beach move with Peter's AI assistant">
                    <span className="image-page-kicker">Curated Palm Beach Collection</span>
                    <h1>
                      <span>Ready for your</span>
                      <span className="image-page-title-accent">Palm Beach Move?</span>
                    </h1>
                    <h2>With {SITE_CONFIG.AGENT.NAME}, {SITE_CONFIG.AGENT.BROKERAGE}</h2>
                    <p>A unique collection of rare and exclusive properties. Customize your search for the perfect property by using our advanced search technology.</p>
                  </Link>
                  <div className="featured-properties-widget">
                    <h3>Featured Properties</h3>
                    {featuredProperties.length > 0 && (
                      <>
                        <div
                          className="property-carousel"
                          onTouchStart={(e) => { propertyTouchStartX.current = e.touches[0].clientX; }}
                          onTouchEnd={(e) => {
                            if (propertyTouchStartX.current === null) return;
                            const delta = e.changedTouches[0].clientX - propertyTouchStartX.current;
                            if (Math.abs(delta) > 40) {
                              setCurrentPropertyIndex((prev) =>
                                delta < 0
                                  ? (prev + 1) % featuredProperties.length
                                  : (prev - 1 + featuredProperties.length) % featuredProperties.length
                              );
                            }
                            propertyTouchStartX.current = null;
                          }}
                        >
                          <div className="property-carousel-track">
                            {featuredProperties.map((property, index) => {
                              let offset = index - currentPropertyIndex;
                              const halfwayPoint = Math.ceil(featuredProperties.length / 2);
                              if (offset > halfwayPoint) offset -= featuredProperties.length;
                              if (offset < -halfwayPoint) offset += featuredProperties.length;
                              const isActive = offset === 0;
                              return (
                                <button
                                  key={property.listing_key || index}
                                  type="button"
                                  className={`featured-property-card ${isActive ? 'active' : ''}`}
                                  style={{
                                    transform: `translateX(${offset * 74}%) translateZ(${isActive ? 28 : -80}px) scale(${isActive ? 1.035 : 0.78}) rotateY(${offset * -3}deg)`,
                                    opacity: Math.abs(offset) > 2 ? 0 : isActive ? 1 : 0.34,
                                    pointerEvents: isActive ? 'auto' : 'none',
                                    zIndex: 10 - Math.abs(offset)
                                  }}
                                  tabIndex={isActive ? 0 : -1}
                                  aria-hidden={!isActive}
                                  aria-label={`View featured property at ${formatPropertyAddress(property)}`}
                                  onClick={() => {
                                    if (isActive) {
                                      window.history.pushState(null, '', `/listing/${property.listing_key}`);
                                      openPropertyFromSummary(property);
                                    } else {
                                      setCurrentPropertyIndex(index);
                                    }
                                  }}
                                >
                                  <img src={property.first_photo_url} alt={formatStreetAddress(property)} decoding="async" />
                                  <span className="property-card-frame" aria-hidden="true"></span>
                                  <span className="property-residence-tag">
                                    {property.featured_source === 'market' ? 'South Florida Selection' : 'Integritos Featured'}
                                  </span>
                                  <span className="property-info">
                                    <span className="property-price">${property.current_price?.toLocaleString()}</span>
                                    <span className="property-address">{formatPropertyAddress(property)}</span>
                                    <span className="property-view-link">View Property <span aria-hidden="true">↗</span></span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div className="property-gallery-footer">
                          <div className="property-gallery-status" aria-live="polite">
                            <span className="property-gallery-count"><strong>{String(currentPropertyIndex + 1).padStart(2, '0')}</strong> / {String(featuredProperties.length).padStart(2, '0')}</span>
                            <button
                              type="button"
                              className="property-gallery-progress"
                              aria-label="Show next featured property"
                              onClick={(event) => {
                                event.stopPropagation();
                                setCurrentPropertyIndex((prev) => (prev + 1) % featuredProperties.length);
                              }}
                            >
                              <span style={{width: `${((currentPropertyIndex + 1) / featuredProperties.length) * 100}%`}}></span>
                            </button>
                          </div>
                          <div className="property-nav-buttons">
                            <button aria-label="Previous property" onClick={(e) => { e.stopPropagation(); setCurrentPropertyIndex((prev) => (prev - 1 + featuredProperties.length) % featuredProperties.length); }}>←</button>
                            <div className="property-dots">
                              {featuredProperties.map((_, index) => (
                                <button
                                  key={index}
                                  aria-label={`Go to property ${index + 1}`}
                                  className={`property-dot ${index === currentPropertyIndex ? 'active' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); setCurrentPropertyIndex(index); }}
                                />
                              ))}
                            </div>
                            <button aria-label="Next property" onClick={(e) => { e.stopPropagation(); setCurrentPropertyIndex((prev) => (prev + 1) % featuredProperties.length); }}>→</button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="e-image-page" id="home-value">
              {showEPage && (
                <>
                  <video
                    ref={homeValueVideoRef}
                    className="home-value-video"
                    muted
                    loop
                    playsInline
                    preload="none"
                    poster={landingPageConfig.EDITORIAL_IMAGES.CONDO_INTERIOR}
                    aria-hidden="true"
                  >
                    <source src={landingPageConfig.VIDEOS.HOME_VALUE} type="video/mp4" />
                  </video>
                  <div className="home-value-video-overlay" />
                  <div className="home-value-section">
                  <div className="home-value-content-col">
                    <span className="home-value-eyebrow">Free for Homeowners</span>
                    <h2 className="e-image-heading">What's Your Home Worth?</h2>
                    <p className="home-value-subtext">Get a personalized property value assessment backed by local market knowledge and real-world insight — confidential, complimentary, and with no obligation.</p>
                    <button className="home-value-btn" onClick={() => setShowHomeValueModal(true)}>Get My Free Estimate</button>
                    <span className="home-value-assurance">Confidential · Local · No obligation</span>
                  </div>
                  </div>
                </>
              )}
            </div>
            <div className={`city-tiles-page ${showCityTiles ? 'visible' : ''}`} id="service-areas">
                <div className="city-tiles-heading">
                  <span className="city-tiles-eyebrow">The South Florida Guide</span>
                  <h2 className="city-tiles-title">Explore Our <em>Service Areas</em></h2>
                  <p>Extraordinary communities. Unmatched lifestyle. Local expertise.</p>
                </div>
                <div className="city-tiles-grid">
                  {SERVICE_AREAS.map((area, index) => (
                    <Link
                      key={area.city}
                      to={`/neighborhood/${area.slug}`}
                      className={`city-tile ${index === 0 ? 'city-tile-featured' : ''}`}
                      style={{'--city-image': `url("${area.imageUrl}")`, '--tile-index': index}}
                      aria-label={`Explore real estate in ${area.label}, Florida`}
                    >
                      <img className="city-tile-image" src={area.imageUrl} alt="" decoding="async" />
                      <span className="city-tile-frame"></span>
                      <span className="city-tile-content">
                        <span className="city-tile-heading-row">
                          <span className="city-tile-marker"></span>
                          <span className="city-tile-label">{area.label}</span>
                        </span>
                        <span className="city-tile-descriptor">{area.descriptor}</span>
                        <span className="city-tile-link">Explore the area <span aria-hidden="true">↗</span></span>
                      </span>
                    </Link>
                  ))}
                </div>
                <LocalResourceLinks variant="landing" />
          </div>
          </>
        )}
        <div className={`chat-wrapper ${!showIntroVideo ? 'fullscreen' : 'hidden'}`}>
          <ActionButtons
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            user={user}
            isAdmin={isAdmin}
            setUser={setUser}
            setIsAdmin={setIsAdmin}
            hidePrompts={false}
            isFullPage={!showIntroVideo}
            onBack={() => setShowIntroVideo(true)}
          />
        </div>
      </div>

      {showMapPage && (
        <div className="full-map-page">
          <button
            className="close-map-page"
            onClick={() => setShowMapPage(false)}
          >
            ← Back to Chat
          </button>
          <ActionButtons
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            isMapPage={true}
            onMapPageClose={() => setShowMapPage(false)}
            user={user}
            isAdmin={isAdmin}
            setUser={setUser}
            setIsAdmin={setIsAdmin}
          />
        </div>
      )}
      {showGlobeMap && (
        <div className="full-map-page">
          <ActionButtons
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            isGlobeMap={true}
            onGlobeMapResultsChange={setShowGlobeMapResults}
            onGlobeMapClose={() => {sessionStorage.removeItem('globeMapData');window.history.replaceState(window.history.state,'','/');setSelectedCity(null);
              setShowGlobeMap(false);
              setShowGlobeMapResults(false);
              setShowIntroVideo(true);
              setSelectedCity(null);
              if (directSearch || directListing) navigate('/');
            }}
            directListingKey={directListing ? listingKey : null}
            initialCity={selectedCity}
            user={user}
            isAdmin={isAdmin}
            setUser={setUser}
            setIsAdmin={setIsAdmin}
          />
        </div>
      )}
      {showBlogs && (
        <BlogsPage onClose={() => setShowBlogs(false)} />
      )}
      {showReviews && (
        <ReviewsPage onClose={() => setShowReviews(false)} />
      )}

      <footer className={`landing-footer ${(!showIntroVideo || showGlobeMap || showAgentSection) ? 'hidden' : ''}`}>
        <a href="/privacy-policy" className="footer-link">Privacy Policy</a>
        <span className="footer-separator"> | </span>
        <a href="/terms-of-service" className="footer-link">Terms of Service</a>
        <span className="footer-separator"> | </span>
        <button
          onClick={() => setShowMLSDisclosure(true)}
          className="footer-link mls-disclosure-link"
        >
          MLS Disclosure
        </button>
        <span className="footer-separator-logo"> | </span>
        <a href="https://agentwinds.com" target="_blank" rel="noopener noreferrer">
          <img src={landingPageConfig.FOOTER_LOGO} alt="Websites by AW" className="footer-logo" />
        </a>
      </footer>

      {showMLSDisclosure && (
        <div className="modal-overlay" onClick={() => setShowMLSDisclosure(false)}>
          <div className="mls-disclosure-modal" onClick={(e) => e.stopPropagation()}>
            <h3>MLS Disclosure</h3>
            <p>{SITE_CONFIG.MLS.DISCLAIMER}</p>
            <p>
              All information is deemed reliable but not guaranteed. All properties are subject to prior sale, change or withdrawal.
              Neither listing broker(s) nor information provider(s) shall be responsible for any typographical errors,
              misinformation, or misprints and shall be held totally harmless. Listing(s) information is provided for
              consumers' personal, non-commercial use and may not be used for any purpose other than to identify
              prospective properties consumers may be interested in purchasing. The data relating to real estate for
              sale on this website comes in part from the Internet Data Exchange program of {SITE_CONFIG.MLS.NAME}.
              Real estate listings held by brokerage firms other than {SITE_CONFIG.AGENT.BROKERAGE},
              may be marked with the Internet Data Exchange logo, and detailed information about those properties will
              include the name of the listing broker(s) when required by the MLS.
            </p>
            <button onClick={() => setShowMLSDisclosure(false)} className="close-disclosure-btn">
              Close
            </button>
          </div>
        </div>
      )}

      {showConnectSuccess && (
        <div className="connect-success-notification">
          <div className="connect-success-content">
            <span className="connect-success-icon">✓</span>
            <span className="connect-success-text">Thank you! We'll be in touch soon.</span>
          </div>
        </div>
      )}

      {showHomeValueSuccess && (
        <div className="connect-success-notification">
          <div className="connect-success-content">
            <span className="connect-success-icon">✓</span>
            <span className="connect-success-text">Got it! Peter will personally reach out to provide your free home evaluation.</span>
          </div>
        </div>
      )}

      {showHomeValueModal && (
        <div className="modal-overlay" onClick={() => setShowHomeValueModal(false)}>
          <div className="connect-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Free Home Value Estimate</h3>
            <p className="connect-modal-intro">Start with the property address so Peter can review the right home—not just a ZIP code.</p>
            <form onSubmit={handleHomeValueSubmit}>
          <HoneypotField />
              <input type="text" placeholder="Full Name" value={homeValueForm.name} onChange={(e) => setHomeValueForm({...homeValueForm, name: e.target.value})} required />
              <input type="email" placeholder="Email" value={homeValueForm.email} onChange={(e) => setHomeValueForm({...homeValueForm, email: e.target.value})} required />
              <input type="tel" placeholder="Phone Number" value={homeValueForm.phoneNumber} onChange={(e) => setHomeValueForm({...homeValueForm, phoneNumber: e.target.value})} required />
              <input type="text" placeholder="Property address" autoComplete="street-address" value={homeValueForm.address} onChange={(e) => setHomeValueForm({...homeValueForm, address: e.target.value})} required />
              <input type="text" placeholder="City" aria-label="Property city" autoComplete="address-level2" value={homeValueForm.city} onChange={(e) => setHomeValueForm({...homeValueForm, city: e.target.value})} required />
              <input type="text" placeholder="State (e.g. FL)" aria-label="Property state" autoComplete="address-level1" maxLength={2} pattern="[A-Za-z]{2}" title="Enter the two-letter state abbreviation" value={homeValueForm.state} onChange={(e) => setHomeValueForm({...homeValueForm, state: e.target.value.toUpperCase()})} required />
              <input type="text" placeholder="ZIP Code" autoComplete="postal-code" inputMode="numeric" pattern="[0-9]{5}(-[0-9]{4})?" title="Enter a five-digit ZIP code or ZIP+4" value={homeValueForm.postalCode} onChange={(e) => setHomeValueForm({...homeValueForm, postalCode: e.target.value})} required />
              <label className="sms-consent-control">
                <input type="checkbox" checked={homeValueForm.smsConsent} onChange={(e) => setHomeValueForm({...homeValueForm, smsConsent: e.target.checked})} />
                <span>I agree to receive text messages about this request. Consent is not a condition of service. Message and data rates may apply; reply STOP to opt out.</span>
              </label>
              <button type="submit" disabled={submittingHomeValue}>{submittingHomeValue ? 'Submitting...' : 'Submit'}</button>
              <button type="button" onClick={() => setShowHomeValueModal(false)} className="cancel-btn">Cancel</button>
            </form>
          </div>
        </div>
      )}

      {showConnectModal && (
        <div className="modal-overlay" onClick={() => setShowConnectModal(false)}>
          <div className="connect-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Ready to Connect?</h3>
            <form onSubmit={handleConnectSubmit}>
          <HoneypotField />
              <input
                type="text"
                placeholder="Full Name"
                value={connectForm.name}
                onChange={(e) => setConnectForm({...connectForm, name: e.target.value})}
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={connectForm.email}
                onChange={(e) => setConnectForm({...connectForm, email: e.target.value})}
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={connectForm.phoneNumber}
                onChange={(e) => setConnectForm({...connectForm, phoneNumber: e.target.value})}
                required
              />
              <input
                type="text"
                placeholder="ZIP Code"
                value={connectForm.postalCode}
                onChange={(e) => setConnectForm({...connectForm, postalCode: e.target.value})}
                required
              />
              <label className="sms-consent-control">
                <input type="checkbox" checked={connectForm.smsConsent} onChange={(e) => setConnectForm({...connectForm, smsConsent: e.target.checked})} />
                <span>I agree to receive text messages about this request. Consent is not a condition of service. Message and data rates may apply; reply STOP to opt out.</span>
              </label>
              <select
                value={connectForm.serviceReq}
                onChange={(e) => setConnectForm({...connectForm, serviceReq: e.target.value})}
                required
              >
                <option value="buying">Buying</option>
                <option value="selling">Selling</option>
                <option value="both">Both</option>
              </select>
              <button type="submit" disabled={submittingConnect}>
                {submittingConnect ? 'Submitting...' : 'Submit'}
              </button>
              <button type="button" onClick={() => setShowConnectModal(false)} className="cancel-btn">
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}

      <PropertyDetailModal
        propertyDetail={propertyDetail}
        propertyDetailImages={propertyDetailImages}
        imagesLoading={imagesLoading}
        onClose={() => {
          closePropertyDetail();
          window.history.pushState(null, '', '/');
        }}
        user={user}
        isAdmin={isAdmin}
        likedProperties={likedProperties}
        setLikedProperties={setLikedProperties}
      />

    </div>
  );
};

export default LandingPage;
